<template>
    <div v-if="json" class="block-two-panel-callouts">
        <div class="capper">
            <div class="capper-padded-2x">
                <div class="items">
                    <template v-for="(item, index) in json" :key="`tpc${index}`">
                        <div class="item">
                            <img :alt="item?.topIcon?.[0]?.title" ref="itemImgs" :src="item?.topIcon?.[0]?.url" />
                            <h2 ref="itemH2s">{{ item.title }}</h2>
                            <div ref="itemCaptions" class="caption" v-html="item.caption"></div>
                            <div ref="itemBtns" class="btn-outer">
                                <Link
                                    v-if="item?.linkout?.url"
                                    :href="item?.linkout?.url"
                                    :target="item?.linkout?.target"
                                    :label="gh.removeTags(item?.linkout?.label)"
                                    :isButton="true"
                                />
                            </div>
                        </div>
                        <div v-if="index === 0" class="vertical-line" ref="verticalLine">
                            <Lines type="blank" class="show-mobile block"></Lines>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from "@/plugins/gsap";
import SplitText from "@/plugins/gsap/SplitText";
import gh from "@/helpers/gh";
import Link from "@/components/pieces/Link";
import Lines from "@/components/pieces/Lines";

gsap.registerPlugin(SplitText);

export default {
    name: "TwoPanelCallouts",
    components: {
        Link,
        Lines
    },
    props: {
        json: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            gh: gh,
        };
    },
    mounted() {
        setTimeout(this.initSetup, 50)
        // Use an IntersectionObserver to trigger the timeline when 35% of the panel is visible
        const observerOptions = { threshold: 0.35 };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.animate();
                    obs.disconnect();
                }
            });
        }, observerOptions);

        observer.observe(this.$el);
    },
    methods: {
        initSetup() {
            // Use the component's root element to scope your selectors
            let imgs = this.$el.querySelectorAll(".item img");
            let h2s = this.$el.querySelectorAll(".item h2");
            let captions = this.$el.querySelectorAll(".item .caption");
            let btns = this.$el.querySelectorAll(".item .btn-outer");
            let verticalLine = this.$el.querySelector(".vertical-line");

            // Set opacity to 0 for all images
            if (imgs) {
                imgs.forEach((el) => {
                    el.style.opacity = 0;
                });
            }

            // Set opacity to 0 for all h2 elements
            if (h2s) {
                h2s.forEach((el) => {
                    el.style.opacity = 0;
                });
            }

            // Set opacity to 0 for all caption elements
            if (captions) {
                captions.forEach((el) => {
                    el.style.opacity = 0;
                });
            }

            // Set opacity to 0 for all button containers
            if (btns) {
                btns.forEach((el) => {
                    el.style.opacity = 0;
                });
            }

            // Set opacity to 0 for the vertical line (if it exists)
            if (verticalLine) {
                verticalLine.style.opacity = 0;
            }
        },

        animate() {
            // Create the main timeline
            const timeline = gsap.timeline();

            // Retrieve the arrays of refs for each element type
            const imgs = this.$refs.itemImgs;
            const h2s = this.$refs.itemH2s;
            const captions = this.$refs.itemCaptions;
            const btns = this.$refs.itemBtns;


            // Loop through each callout item to add animations with fixed start times
            for (let i = 0; i < imgs.length; i++) {
                // Add a base label for each item. Each item is staggered by 1 second.
                timeline.addLabel(`item${i}`, i * 1);

                // Animate the image at fixed start time 0 (relative to each item label)
                timeline.fromTo(
                    imgs[i],
                    { opacity: 0, scale: 0, y: 15 },
                    { opacity: 1, scale: 1, y: 0, duration: 0.75, ease: "power2.out" },
                    0
                );

                // Animate the heading (h2) with SplitText starting at fixed 0.2 sec
                let splitHeading = new SplitText(h2s[i], { type: "words" });
                gsap.set(h2s[i], { opacity: 1 });
                timeline.fromTo(
                    splitHeading.words,
                    { opacity: 0, y: 15 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.75,
                        ease: "power2.out",
                        stagger: 0.1,
                    },
                    0.2
                );

                // Animate the caption at fixed start time 0.4 sec
                timeline.fromTo(
                    captions[i],
                    { opacity: 0, y: 20 },
                    { opacity: 1, y: 0, duration: 0.75, ease: "power2.out" },
                    0.4
                );

                // Animate the button container at fixed start time 0.6 sec
                timeline.fromTo(
                    btns[i],
                    { opacity: 0, y: 20 },
                    { opacity: 1, y: 0, duration: 0.75, ease: "power2.out" },
                    0.6
                );
            }

            // Optionally animate the vertical line if present (only for the first item)
            if (this.$refs.verticalLine) {
                timeline.fromTo(
                    this.$refs.verticalLine,
                    { opacity: 0, scaleY: 0.5 },
                    { opacity: 1, scaleY: 1, duration: 0.75, ease: "power2.out" },
                    0
                );
            }
        },
    },
};
</script>
